import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authResendVerification } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function RegisterSteuerberater() {
  const { registerOrg } = useAuth();
  const [form, setForm] = useState({ company: "", name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resending, setResending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) {
      toast.error("Bitte geben Sie den Namen Ihrer Kanzlei an.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      const res = await registerOrg({
        account_type: "steuerberater",
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        company: form.company.trim(),
      });
      setSentTo(res?.email || form.email.trim());
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Registrierung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authResendVerification(sentTo);
      toast.success("Bestätigungs-E-Mail wurde erneut gesendet.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setResending(false);
    }
  };

  if (sentTo) {
    return (
      <AuthShell
        title="Fast geschafft!"
        subtitle="Bitte bestätigen Sie Ihre E-Mail-Adresse."
        footer={
          <span>
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
              Zur Anmeldung
            </Link>
          </span>
        }
      >
        <div className="space-y-4 text-center" data-testid="register-confirmation">
          <MailCheck className="h-12 w-12 text-primary mx-auto" />
          <p className="text-sm text-foreground">
            Wir haben einen Bestätigungslink an <strong>{sentTo}</strong> gesendet. Bitte öffnen Sie
            die E-Mail und klicken Sie auf <strong>„E-Mail bestätigen"</strong>, um Ihr Konto zu
            aktivieren.
          </p>
          <p className="text-xs text-muted-foreground">
            Der Link ist 12 Stunden gültig. Keine E-Mail erhalten? Bitte auch den Spam-Ordner prüfen.
          </p>
          <Button
            variant="secondary"
            className="w-full border border-border"
            onClick={resend}
            disabled={resending}
            data-testid="resend-verification-button"
          >
            {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : "E-Mail erneut senden"}
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Registrieren als Steuerberater"
      subtitle="Erstellen Sie Ihr BeSca-Kanzlei-Konto."
      footer={
        <span>
          Bereits registriert?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
            Zur Anmeldung
          </Link>
        </span>
      }
    >
      <div
        className="mb-4 border-l-2 border-primary bg-primary/5 px-3 py-2.5 text-sm text-foreground"
        data-testid="register-stb-promo"
      >
        <span className="font-medium">Der erste Monat ist kostenlos.</span>{" "}
        Sie können bis zu 3 Mandanten und 2 Ihrer Mitarbeiter einladen, um BeSca vollumfänglich zu
        testen. Angabe der Kreditkarte ist nicht nötig.
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="register-stb-form">
        <div className="space-y-2">
          <Label htmlFor="company">Kanzlei</Label>
          <Input
            id="company"
            value={form.company}
            onChange={set("company")}
            required
            autoComplete="organization"
            data-testid="register-stb-company"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            data-testid="register-stb-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">E-Mail</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={set("email")}
            required
            autoComplete="email"
            data-testid="register-stb-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            value={form.password}
            onChange={set("password")}
            required
            autoComplete="new-password"
            data-testid="register-stb-password"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Passwort bestätigen</Label>
          <Input
            id="confirm"
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            required
            autoComplete="new-password"
            data-testid="register-stb-confirm"
          />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-stb-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kanzlei-Konto erstellen"}
        </Button>
      </form>
    </AuthShell>
  );
}
